import qs from 'qs'

import { useCallback, useRef, useState } from 'react'
import { buildTableSearchParams } from '~/components/dynamic_table/helpers/browserState'
import { csrfToken } from '~/utils/csrf'

export default function useServerSideSearch({ baseUrl, method = 'get' }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const controllerRef = useRef(null)

  const fetchData = useCallback(
    async ({ query, sort, page, perPage, options = { loading: true } } = {}) => {
      const { loading: loaderEnabled } = options

      if (loaderEnabled) setLoading(true)
      setError(null)

      try {
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Remember-Filters': 'true',
        }

        const [locationPath, urlParams] = (baseUrl || '').split('?', 2)
        const { searchParams } = buildTableSearchParams({
          query,
          sort,
          page,
          per_page: perPage,
          locationPath,
          urlParams,
        })

        // Abort previous controller if present
        if (controllerRef.current) controllerRef.current.abort()
        controllerRef.current = new AbortController()

        let fetchOptions = {
          headers,
          signal: controllerRef.current.signal,
        }

        let url = locationPath

        if (method.toLowerCase() === 'post') {
          // For POST, build body directly from query and other params (preserve array structure)
          const body = qs.parse(searchParams)

          const token = csrfToken()
          if (token) {
            headers['X-CSRF-Token'] = token
          }

          fetchOptions = {
            ...fetchOptions,
            method: 'POST',
            body: JSON.stringify(body),
          }
        } else {
          // GET: use query string as before
          url = `${locationPath}?${searchParams}`
        }

        const response = await fetch(url, fetchOptions)
        const json = await response.json()

        // Update history in caller by returning searchParams
        const historySearchParams = searchParams

        if (loaderEnabled) setLoading(false)

        return { data: json.data, meta: json.meta, searchParams: historySearchParams }
      } catch (err) {
        if (err.name === 'AbortError') return { aborted: true }
        setError(err)
        if (loaderEnabled) setLoading(false)
        throw err
      }
    },
    [baseUrl, method],
  )

  const abort = useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort()
  }, [])

  return { fetchData, loading, error, abort }
}
